import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { api } from '../lib/api';

// ---------------------------------------------------------------------------
// NotificationBell — a small bell icon with an unread dot. When clicked it
// opens a calm, cream dropdown listing recent notifications. Each item has
// title, optional body + a subtle relative timestamp; clicking an item marks
// it read and (if a link is present) navigates there.
// ---------------------------------------------------------------------------

const POLL_MS = 30000;

function timeAgo(iso) {
  try {
    const then = new Date(iso);
    const sec = Math.floor((Date.now() - then.getTime()) / 1000);
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
    return then.toLocaleDateString();
  } catch {
    return '';
  }
}

export function NotificationBell({ onDark = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);
  const nav = useNavigate();

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnread(data.unread_count || 0);
    } catch (_) {
      /* silent — bell stays hidden */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications');
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (_) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll unread count every 30s while mounted.
  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, POLL_MS);
    return () => clearInterval(id);
  }, [loadCount]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) await loadList();
  };

  const markAllRead = async () => {
    try {
      const { data } = await api.post('/notifications/mark-read', {});
      setUnread(data.unread_count || 0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_) {
      /* silent */
    }
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) {
        await api.post('/notifications/mark-read', { ids: [n.notification_id] });
        setItems((prev) =>
          prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)),
        );
        setUnread((c) => Math.max(0, c - 1));
      }
    } catch (_) {
      /* ignore */
    }
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  };

  const iconColor = onDark
    ? 'text-[#F7F3E8]/80 hover:text-[#C5A059]'
    : 'text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))]';

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={toggle}
        className={`relative p-2 transition-colors ${iconColor}`}
        aria-label="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell strokeWidth={1.6} size={20} />
        {unread > 0 && (
          <span
            className="absolute top-1 right-1 block rounded-full bg-[hsl(var(--accent))] text-[#1A3626] text-[9px] font-medium leading-none min-w-[14px] h-[14px] px-[3px] flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-3 w-[340px] max-w-[calc(100vw-2rem)] bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.1)] shadow-lg z-50"
          role="dialog"
          aria-label="Notifications"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--primary)/0.08)]">
            <div className="ml-overline">Notifications</div>
            <button
              type="button"
              onClick={markAllRead}
              className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))] transition-colors disabled:opacity-40"
              disabled={unread === 0}
              data-testid="notification-mark-all-read-btn"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && (
              <div className="px-5 py-8 text-center text-xs text-[hsl(var(--primary)/0.5)]">
                Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-5 py-10 text-center" data-testid="notification-empty">
                <div className="ml-overline">All quiet</div>
                <p className="ml-caption mt-4 max-w-[260px] mx-auto">
                  When family requests access or adds a new memory, you'll see it here.
                </p>
              </div>
            )}
            {!loading &&
              items.map((n) => (
                <button
                  key={n.notification_id}
                  type="button"
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.notification_id}`}
                  className={`w-full text-left px-5 py-4 border-b border-[hsl(var(--primary)/0.06)] last:border-b-0 transition-colors hover:bg-[hsl(var(--surface))] ${
                    n.read ? '' : 'bg-[hsl(var(--accent)/0.06)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {!n.read && (
                      <span
                        aria-hidden
                        className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-[14px] leading-snug text-[hsl(var(--primary))]">
                        {n.title}
                      </div>
                      {n.body && (
                        <div className="text-[12px] text-[hsl(var(--primary)/0.65)] mt-1 leading-relaxed line-clamp-2">
                          {n.body}
                        </div>
                      )}
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.45)] mt-2">
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
